import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  PageHeader,
  Button,
  StatusBadge,
  ConfirmationModal,
} from "../../../components/common";
import {
  useAppDispatch,
  useAppSelector,
  submitArtworkThunk,
  getStudentSubmittedArtworksThunk,
  getPreviewLinkThunk,
  getDownloadLinkThunk,
} from "../../../redux";
import { showSuccess, showError } from "../../../services";
import { extractWorkdriveResourceId } from "../../../utils";
import uploadIcon from "../../../assets/icons/upload.svg";
import eyeIcon from "../../../assets/icons/eye.svg";
import downloadIcon from "../../../assets/icons/download.svg";
import "./ArtworkSubmission.css";

const MAX_FILE_SIZE_MB = 200;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_FILES = 10;
const ALLOWED_EXTENSIONS = [".pdf", ".png", ".jpg", ".jpeg", ".webp", ".zip"];

const ArtworkSubmission: React.FC = () => {
  const [assignmentName, setAssignmentName] = useState<string>("");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [downloadTarget, setDownloadTarget] = useState<{
    workdriveUrl?: string;
    fileName?: string;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { studentArtworks, isSubmitting, isLoading } = useAppSelector(
    (state) => state.artwork,
  );

  useEffect(() => {
    if (user?.id) {
      dispatch(getStudentSubmittedArtworksThunk(user.id));
    }
  }, [dispatch, user?.id]);

  const totalSizeBytes = selectedFiles.reduce(
    (sum, file) => sum + file.size,
    0,
  );
  const totalSizeMB = (totalSizeBytes / (1024 * 1024)).toFixed(2);

  const handlePreview = (resourceId: string) => {
    if (!resourceId) return;
    dispatch(getPreviewLinkThunk(resourceId)).then((res) => {
      if (getPreviewLinkThunk.fulfilled.match(res)) {
        const previewUrl = res.payload.previewUrl;
        if (previewUrl) {
          window.open(previewUrl, "_blank");
        }
      }
    });
  };

  const handleDownload = (workdriveUrl?: string, fileName?: string) => {
    if (!workdriveUrl) return;
    dispatch(getDownloadLinkThunk(workdriveUrl)).then((res) => {
      if (getDownloadLinkThunk.fulfilled.match(res)) {
        const url = window.URL.createObjectURL(res.payload);
        const a = document.createElement("a");
        a.href = url;
        if (fileName) a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    });
  };

  const handleFileSelect = (filesToAdd: FileList | File[] | null) => {
    if (!filesToAdd) return;
    const arrayFiles = Array.from(filesToAdd);

    const validFiles: File[] = [];
    let invalidExtensionCount = 0;
    let oversizedFileCount = 0;

    arrayFiles.forEach((file) => {
      const ext = "." + file.name.split(".").pop()?.toLowerCase();
      const hasAllowedType =
        ALLOWED_EXTENSIONS.includes(ext) ||
        file.type.startsWith("image/") ||
        file.type.includes("pdf") ||
        file.type.includes("zip");

      if (!hasAllowedType) {
        invalidExtensionCount++;
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        oversizedFileCount++;
      } else {
        validFiles.push(file);
      }
    });

    if (invalidExtensionCount > 0) {
      showError(
        "Some files were skipped due to unsupported file types. Allowed: PDF, Images, ZIP.",
      );
    }
    if (oversizedFileCount > 0) {
      showError(`Files larger than ${MAX_FILE_SIZE_MB} MB were skipped.`);
    }

    const existingNames = new Set(selectedFiles.map((file) => file.name));
    const newUnique = validFiles.filter((file) => !existingNames.has(file.name));
    const availableSlots = Math.max(0, MAX_FILES - selectedFiles.length);

    if (newUnique.length > availableSlots) {
      showError(`A maximum of ${MAX_FILES} files can be submitted.`);
    }

    setSelectedFiles((prev) => [...prev, ...newUnique.slice(0, availableSlots)]);
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!assignmentName.trim()) {
      showError("Please enter the Assignment / Artwork Name.");
      return;
    }
    if (selectedFiles.length === 0) {
      showError("Please attach at least one file for your artwork submission.");
      return;
    }
    if (!user?.id) return;

    const teacherId = user.Allocated_Teacher?.id;

    dispatch(
      submitArtworkThunk({
        assignment_name: assignmentName,
        teacher_id: teacherId,
        files: selectedFiles,
      }),
    ).then((res) => {
      if (submitArtworkThunk.fulfilled.match(res)) {
        showSuccess("Artwork submitted successfully!");
        setAssignmentName("");
        setSelectedFiles([]);
        if (user?.id) {
          dispatch(
            getStudentSubmittedArtworksThunk({
              studentId: user.id,
              force: true,
            }),
          );
        }
      }
    });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
      <div className="artwork-submission-layout">
        <div className="artwork-form-left">
          <Card style={{ padding: "28px" }}>
            <form onSubmit={handleSubmit} className="artwork-form-container">
              <div className="content-field">
                <label htmlFor="assignmentName">
                  Assignment Name / Subject
                </label>
                <input
                  type="text"
                  id="assignmentName"
                  placeholder="Enter assignment title (e.g. Canvas Painting)"
                  value={assignmentName}
                  onChange={(e) => setAssignmentName(e.target.value)}
                  required
                />
              </div>

              <div className="content-field">
                <label>Attachments (PDF, Image, ZIP - Max 200 MB per file)</label>
                <div className="artwork-upload-container">
                  <div
                    className={`artwork-dropzone ${isDragging ? "dragging" : ""}`}
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <img
                      src={uploadIcon}
                      alt="Upload"
                      className="dropzone-icon"
                      width={32}
                      height={32}
                    />
                    <div className="dropzone-text">
                      Click or drag & drop files to upload
                    </div>
                    <div className="dropzone-subtext">
                      Select multiple files: PDF, Images (PNG, JPG, WEBP), ZIP
                      (Max 200 MB per file)
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.png,.jpg,.jpeg,.webp,.zip,image/*,application/pdf,application/zip"
                      className="file-input-hidden"
                      onChange={(e) => handleFileSelect(e.target.files)}
                    />
                  </div>

                  <div className="uploaded-files-column">
                    <div className="total-size-info">
                      <span>Attached Files ({selectedFiles.length})</span>
                      <span>Total Size: {totalSizeMB} MB</span>
                    </div>

                    {selectedFiles.length > 0 ? (
                      <div className="selected-files-list">
                        {selectedFiles.map((file, idx) => {
                          const ext =
                            file.name.split(".").pop()?.toUpperCase() || "FILE";
                          const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
                          return (
                            <div key={idx} className="file-preview-card">
                              <div className="file-info">
                                <span className="file-type-badge">{ext}</span>
                                <div className="file-details">
                                  <span className="file-name">{file.name}</span>
                                  <span className="file-size">{sizeMB} MB</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                className="remove-file-btn"
                                onClick={() => handleRemoveFile(idx)}
                                title="Remove file"
                              >
                                &times;
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="empty-files-placeholder">
                        <span className="empty-files-title">No files attached yet</span>
                        <span className="empty-files-subtext">
                          Selected files will appear here
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ marginTop: "12px" }}>
                <Button
                  type="submit"
                  variant="primary"
                  size="medium"
                  disabled={isSubmitting || selectedFiles.length === 0}
                >
                  {isSubmitting ? "Submitting Artwork..." : "Submit Artwork"}
                </Button>
              </div>
            </form>
          </Card>
        </div>

        {/* Submissions History Section */}
        {studentArtworks.length > 0 && (
          <Card
            style={{ padding: "24px" }}
            className="student-submissions-card"
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                fontSize: "1.1rem",
                color: "#29252d",
                fontWeight: 600,
              }}
            >
              My Submitted Artworks
            </h3>
            <div className="student-submissions-table-container">
              <table className="student-submissions-table">
                <thead>
                  <tr>
                    <th>Submission Date</th>
                    <th>Artwork Name</th>
                    <th>Teacher</th>
                    <th>Attachments</th>
                    <th>Status</th>
                    <th>Marks</th>
                    <th>Feedback</th>
                  </tr>
                </thead>
                <tbody>
                  {studentArtworks.map((item) => {
                    const resourceId = extractWorkdriveResourceId(
                      item.workdrive_url,
                    );
                    return (
                      <tr key={item.id}>
                        <td>{item.submission_date}</td>
                        <td>
                          <strong>{item.assignment_name}</strong>
                        </td>
                        <td>{item.teacher_name || "—"}</td>
                        <td>
                          {resourceId ? (
                            <div className="attachments-action-group">
                              <button
                                type="button"
                                className="attachment-action-btn"
                                title="Preview Artwork"
                                aria-label="Preview Artwork"
                                onClick={() => handlePreview(resourceId)}
                              >
                                <img
                                  src={eyeIcon}
                                  alt="Preview"
                                  width={16}
                                  height={16}
                                />
                              </button>
                              <button
                                type="button"
                                className="attachment-action-btn"
                                title="Download Artwork"
                                aria-label="Download Artwork"
                                onClick={() =>
                                  setDownloadTarget({
                                    workdriveUrl: item.workdrive_url,
                                    fileName: item.assignment_name,
                                  })
                                }
                              >
                                <img
                                  src={downloadIcon}
                                  alt="Download"
                                  width={16}
                                  height={16}
                                />
                              </button>
                            </div>
                          ) : (
                            <span style={{ color: "#8c8594" }}>None</span>
                          )}
                        </td>
                        <td>
                          <StatusBadge
                            variant={
                              item.status === "Graded" ? "success" : "warning"
                            }
                          >
                            {item.status}
                          </StatusBadge>
                        </td>
                        <td>
                          {item.marks !== null && item.marks !== undefined ? (
                            <strong style={{ color: "#29252d" }}>
                              {item.marks} / 100
                            </strong>
                          ) : (
                            <span style={{ color: "#8c8594" }}>Unmarked</span>
                          )}
                        </td>
                        <td>{item.feedback || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* ZIP / Attachment Download Confirmation Modal */}
      <ConfirmationModal
        isOpen={Boolean(downloadTarget)}
        title="Confirm Download"
        message={
          downloadTarget?.fileName
            ? `Are you sure you want to download "${downloadTarget.fileName}"?`
            : "Are you sure you want to download this artwork attachment?"
        }
        confirmText="Download"
        cancelText="Cancel"
        confirmVariant="primary"
        onConfirm={() => {
          if (downloadTarget?.workdriveUrl) {
            handleDownload(
              downloadTarget.workdriveUrl,
              downloadTarget.fileName,
            );
          }
          setDownloadTarget(null);
        }}
        onCancel={() => setDownloadTarget(null)}
      />
    </div>
  );
};

export default ArtworkSubmission;
